import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useNotifications,
  useUnreadCount,
  useMarkAllRead,
  useMarkNotificationRead,
  useDeleteNotification,
  useClearReadNotifications,
  useNotificationsRealtime,
  type Notification,
} from './queries';
import { formatShortDateTime } from '@/lib/dates';
import { cn } from '@/lib/cn';

/**
 * Cloche de notifications.
 *
 * Elles étaient enfouies dans « Paramètres », c'est-à-dire nulle part : une
 * notification qu'il faut aller chercher n'en est pas une. Elle vit désormais
 * à côté du bouton d'espace personnel, visible depuis n'importe quelle page.
 *
 * Le compteur s'arrête à 9+ : au-delà, le nombre exact n'aide plus à décider,
 * et il ne tient plus dans la pastille.
 */
export function NotificationBell({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount();
  const notifications = useNotifications(12);
  const markAll = useMarkAllRead();
  const markOne = useMarkNotificationRead();
  const removeOne = useDeleteNotification();
  const clearRead = useClearReadNotifications();
  const root = useRef<HTMLDivElement>(null);

  useNotificationsRealtime();

  // Fermeture au clic extérieur et à Échap : un panneau qui ne se ferme que par
  // son propre bouton est une chausse-trappe sur mobile.
  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = unread.data ?? 0;
  const items = notifications.data ?? [];

  return (
    <div ref={root} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={count > 0 ? `Notifications, ${count} non lues` : 'Notifications'}
        aria-expanded={open}
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-xl border transition-colors',
          open
            ? 'border-ink-950 bg-ink-950 text-white'
            : 'border-ink-200 text-ink-700 hover:border-ink-950 hover:text-ink-950',
        )}
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
          <path
            d="M18 8a6 6 0 1 0-12 0c0 4-1.5 5.5-2 6h16c-.5-.5-2-2-2-6Z"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path d="M10.5 18a1.8 1.8 0 0 0 3 0" strokeWidth="1.7" strokeLinecap="round" />
        </svg>

        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-500 px-1 text-[11px] font-extrabold text-white ring-2 ring-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          // Sur téléphone le panneau s'ancre à l'ÉCRAN, pas à la cloche : la
          // cloche n'est pas au bord droit (le menu est après elle), donc un
          // panneau de 360 px accroché à elle sortait par la gauche et se
          // faisait couper. Le débordement était invisible à la mesure — un
          // dépassement négatif ne crée aucune barre de défilement, il ampute
          // simplement le contenu. Seule la capture d'écran l'a montré.
          className="fixed inset-x-4 top-[68px] z-50 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-lift sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[360px]"
        >
          <div className="flex items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
            <p className="text-sm font-extrabold text-ink-950">Notifications</p>
            <span className="flex items-center gap-3">
              {count > 0 && (
                <button
                  type="button"
                  onClick={() => void markAll.mutateAsync()}
                  disabled={markAll.isPending}
                  className="text-[13px] font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50"
                >
                  Tout marquer lu
                </button>
              )}
              {items.some((item) => item.read_at) && (
                <button
                  type="button"
                  onClick={() => void clearRead.mutateAsync()}
                  disabled={clearRead.isPending}
                  className="text-[13px] font-semibold text-ink-500 hover:text-ink-950 disabled:opacity-50"
                >
                  Effacer les lues
                </button>
              )}
            </span>
          </div>

          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-ink-500">
              Aucune notification pour le moment.
            </p>
          ) : (
            <ul className="max-h-[min(60vh,420px)] divide-y divide-ink-100 overflow-y-auto">
              {items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onOpen={() => {
                    if (!item.read_at) void markOne.mutateAsync(item.id);
                    setOpen(false);
                  }}
                  onDelete={() => void removeOne.mutateAsync(item.id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Une ligne du panneau.
 *
 * Cliquer mène à l'endroit concerné ET marque la notification lue : demander
 * deux gestes pour une seule intention, c'est garantir que la seconde moitié
 * ne sera jamais faite.
 */
function NotificationRow({
  item,
  onOpen,
  onDelete,
}: {
  item: Notification;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const body = (
    <>
      <span className="flex items-start gap-2.5">
        {!item.read_at && (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" aria-hidden />
        )}
        <span className={cn('min-w-0 flex-1', item.read_at && 'pl-[18px]')}>
          <span
            className={cn(
              'block text-[13px] leading-snug',
              item.read_at ? 'font-medium text-ink-700' : 'font-bold text-ink-950',
            )}
          >
            {item.title}
          </span>
          {item.body && (
            <span className="mt-0.5 block break-words text-[13px] leading-snug text-ink-500">
              {item.body}
            </span>
          )}
          <span className="mt-1 block text-[11px] text-ink-400">
            {formatShortDateTime(item.created_at)}
          </span>
        </span>
      </span>
    </>
  );

  // La croix est en dehors du lien : imbriquer un bouton dans un lien produit
  // un balisage invalide, et un clic qui part parfois d'un côté, parfois de
  // l'autre.
  return (
    <li className="relative">
      {item.link ? (
        <Link
          to={item.link}
          onClick={onOpen}
          className="block py-3 pl-4 pr-11 transition-colors hover:bg-ink-50"
        >
          {body}
        </Link>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className="block w-full py-3 pl-4 pr-11 text-left transition-colors hover:bg-ink-50"
        >
          {body}
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Supprimer la notification « ${item.title} »`}
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-950"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor">
          <path d="M6 6l12 12M18 6L6 18" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </li>
  );
}
